import { useEffect } from 'react';
import { NextRouter } from 'next/router';

// The special data's `specialType` field is 'bogo' | 'sale', but the URL sub-route uses
// 'offer' for anything that isn't a 'sale' special. This mirrors the mapping used by the
// link-builders in offer-menu-section.jsx / offer-menu-section-v2.tsx:
//   `specialType === 'sale' ? 'sale' : 'offer'`
const segmentForSpecialType = (specialType?: string | null): 'offer' | 'sale' =>
  specialType === 'sale' ? 'sale' : 'offer';

type UseRedirectMismatchedSpecialTypeProps = {
  router: NextRouter | null;
  // whether we have a confident read on the special's real specialType (i.e. the specials
  // query has resolved and returned data) -- avoids redirecting off of an empty/loading state
  ready: boolean;
  actualSpecialType?: string | null;
  expectedSpecialType: 'bogo' | 'sale';
};

/**
 * The `/specials/offer/[specialId]` and `/specials/sale/[specialId]` pages hardcode a
 * specialType filter on their products query. If a caller links to the wrong sub-route for a
 * given special (e.g. `/specials/offer/<id>` where `<id>` is actually a sale special), the
 * products query silently returns zero results instead of showing the special.
 *
 * This hook compares the special's real specialType (once known) against what the current
 * page expects, and client-side redirects to the sibling route if they don't match --
 * preserving the current app context (dispensary/stores/kiosks/mobile-ecomm/embedded-menu)
 * and all existing query params.
 */
export default function useRedirectMismatchedSpecialType({
  router,
  ready,
  actualSpecialType,
  expectedSpecialType,
}: UseRedirectMismatchedSpecialTypeProps): void {
  useEffect(() => {
    if (!ready || !router || !actualSpecialType) {
      return;
    }

    const correctSegment = segmentForSpecialType(actualSpecialType);
    const currentSegment = segmentForSpecialType(expectedSpecialType);

    if (correctSegment === currentSegment) {
      return;
    }

    void router.replace(
      {
        pathname: router.pathname.replace(`/${currentSegment}/`, `/${correctSegment}/`),
        query: router.query,
      },
      undefined,
      { shallow: false }
    );
  }, [ready, router, actualSpecialType, expectedSpecialType]);
}
